import { useI18n, type MessageKey } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, Field, Modal, RadioGroup } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useState } from 'react';

import { useUpdateOrderStatusMutation } from '../api';
import type { MerchantOrder, OrderStatus } from '../types';

import { useToast } from '@/components/ui/toast/ToastProvider';

/**
 * « Annuler la commande » — until the parcel is at the carrier (À traiter, En préparation, Préparée). The merchant
 * picks a reason (kept in the order's history, in their language); the server puts the reserved stock back on sale
 * and refuses while Jibex holds a parcel for the order (« annulez d'abord le colis »).
 */

/** The statuses an order can be cancelled from — the server's `OrderStateMachine` rows for the merchant. */
export const CANCELLABLE_ORDER_STATUSES: readonly OrderStatus[] = [
  'PAYMENT_CONFIRMED',
  'PROCESSING',
  'PACKED',
];

const REASONS = ['unreachable', 'outOfStock', 'duplicate', 'customerChanged', 'other'] as const;
type Reason = (typeof REASONS)[number];

const REASON_KEY: Record<Reason, MessageKey> = {
  unreachable: 'merchant.cancelOrder.reason.unreachable',
  outOfStock: 'merchant.cancelOrder.reason.outOfStock',
  duplicate: 'merchant.cancelOrder.reason.duplicate',
  customerChanged: 'merchant.cancelOrder.reason.customerChanged',
  other: 'merchant.cancelOrder.reason.other',
};

const MAX_REASON = 500;

export function CancelOrderModal({
  order,
  onClose,
}: {
  order: MerchantOrder | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const updateStatus = useUpdateOrderStatusMutation();
  const [reason, setReason] = useState<Reason | ''>('');
  const [detail, setDetail] = useState('');
  const [error, setError] = useState<string | null>(null);

  // A fresh form for each order opened.
  useEffect(() => {
    setReason('');
    setDetail('');
    setError(null);
  }, [order?.id]);

  async function confirm(): Promise<void> {
    if (order === null) return;
    if (reason === '') {
      setError(t('merchant.cancelOrder.reasonRequired'));
      return;
    }
    if (reason === 'other' && detail.trim() === '') {
      setError(t('merchant.cancelOrder.detailRequired'));
      return;
    }
    const label = t(REASON_KEY[reason]);
    const text =
      reason === 'other'
        ? detail.trim()
        : detail.trim() === ''
          ? label
          : `${label} — ${detail.trim()}`;
    try {
      await updateStatus.mutateAsync({
        orderId: order.id,
        status: 'CANCELLED',
        reason: text.slice(0, MAX_REASON),
      });
      toast.success(t('merchant.cancelOrder.done', { number: order.orderNumber }));
      onClose();
    } catch (failure) {
      setError(t(resolveErrorMessageKey(failure)));
    }
  }

  return (
    <Modal
      open={order !== null}
      title={order !== null ? t('merchant.cancelOrder.title', { number: order.orderNumber }) : ''}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updateStatus.isPending}>
            {t('merchant.cancelOrder.keep')}
          </Button>
          <Button
            variant="danger"
            loading={updateStatus.isPending}
            onClick={() => {
              void confirm();
            }}
          >
            {t('merchant.cancelOrder.confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-inkSoft">{t('merchant.cancelOrder.intro')}</p>
        <RadioGroup
          legend={t('merchant.cancelOrder.reasonLegend')}
          name="cancel-reason"
          options={REASONS.map((value) => ({ value, label: t(REASON_KEY[value]) }))}
          value={reason}
          disabled={updateStatus.isPending}
          onChange={(value) => {
            setReason(value as Reason);
            setError(null);
          }}
        />
        <Field label={t('merchant.cancelOrder.detail')} optional={reason !== 'other'}>
          {(bind) => (
            <textarea
              {...bind}
              rows={2}
              maxLength={300}
              className="ecom-input"
              value={detail}
              disabled={updateStatus.isPending}
              onChange={(event) => {
                setDetail(event.target.value);
                setError(null);
              }}
            />
          )}
        </Field>
        {error !== null ? (
          <p role="alert" className="text-sm text-critical">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
